import type { PermitTypeUnit } from '@contracts/permitType/PermitType';
import { getCheckedKeys } from '@src/common/utils/typeUtils';
import type { FormConditionSheet } from '@src/permits/completePermit/fields/ConditionSheets';
import {
  ConditionSheetsField,
  ShowInstructionsToClientField,
} from '@src/permits/fields/Fields';
import { PERMIT_TYPE_DEFAULT_FIELDS } from '@src/permitTypesManager/PermitTypeDefaultFields';
import type { PermitTypeDefinition } from '@src/permitTypesManager/PermitTypeDefinition';
import type { PermitTypeFields } from '@src/permitTypesManager/PermitTypeFields';
import { CustomDocumentPdfFields } from '@src/permitTypesManager/permitTypeSections/CustomDocumentPdfFields';
import {
  CurrencyField,
  HighPriorityField,
  JurisdictionField,
  JurisdictionUrlField,
  NameField,
  ShowToClientField,
  TypeField,
  UnitSystemFields,
  ValidateExpirationDateField,
  ValidateExpirationTimeField,
} from '@src/permitTypesManager/permitTypeSections/PermitDefinitionFields';
import { PermitInstructionsField } from '@src/permitTypesManager/permitTypeSections/PermitInstructionsField';

const NOT_A_PERMIT_FIELD = [
  JurisdictionField.fieldName,
  TypeField.fieldName,
  CurrencyField.fieldName,
  NameField.fieldName,
  JurisdictionUrlField.fieldName,
  HighPriorityField.fieldName,
  ValidateExpirationDateField.fieldName,
  ValidateExpirationTimeField.fieldName,
  ShowToClientField.fieldName,
  CustomDocumentPdfFields.fieldName,
  PermitInstructionsField.fieldName,
  ConditionSheetsField.fieldName,
] as const;

type PermitTypeEntries = {
  [key: string]: string | boolean | FormConditionSheet[];
};

const getPermitFields = (data: PermitTypeEntries) => {
  const filteredFields: Record<string, boolean> = {};
  for (const key of Object.keys(data)) {
    if (!NOT_A_PERMIT_FIELD.includes(key) && typeof data[key] === 'boolean') {
      filteredFields[key] = data[key];
    }
  }
  return filteredFields;
};

export const PermitTypeAssembler = {
  toDTO: (data: PermitTypeFields): PermitTypeDefinition => {
    const filteredFields = getPermitFields(
      data as unknown as PermitTypeEntries,
    );
    const selectedFields = getCheckedKeys(filteredFields);
    const fieldsForPermitType = Array.from(
      new Set([...selectedFields, ...PERMIT_TYPE_DEFAULT_FIELDS]),
    );
    return {
      jurisdiction: data[JurisdictionField.fieldName] as string,
      name: data[NameField.fieldName] as string,
      jurisdictionUrl: data[JurisdictionUrlField.fieldName] as
        | string
        | undefined,
      type: data[TypeField.fieldName] as string,
      currency: data[CurrencyField.fieldName] as string,
      highPriority: data[HighPriorityField.fieldName] as boolean,
      showToClient: data[ShowToClientField.fieldName] as boolean,
      validateExpirationDate: data[
        ValidateExpirationDateField.fieldName
      ] as boolean,
      validateExpirationTime: data[
        ValidateExpirationTimeField.fieldName
      ] as boolean,
      showInstructionsToClient: data[
        ShowInstructionsToClientField.fieldName
      ] as boolean,
      instructions: data[PermitInstructionsField.fieldName] as string,
      fields: fieldsForPermitType,
      conditionSheetsIds: (
        data[ConditionSheetsField.fieldName] as FormConditionSheet[]
      ).map((sheet) => sheet.id),
      unit: data[UnitSystemFields.fieldName] as PermitTypeUnit,
    };
  },
};
